import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Alert } from "@mui/material";
import { toast } from "react-toastify";
import { useProgramCategories, usePrograms } from "../../hooks/useRedux";
import ProgramForm from "./ProgramForm";
import { Program } from "../../store/slices/types";

const EditProgram: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { categories: programCategories } = useProgramCategories();
  const { programs, updateProgram, loading } = usePrograms();

  const program = programs.find(
    (p) => (p._id || p.id) === id
  ) as Program | undefined;

  const handleSave = async (programData: Partial<Program>) => {
    if (!id) {
      toast.error("Invalid program ID");
      return;
    }

    try {
      await updateProgram(id, programData);
      toast.success("program is updated");
      navigate("/programs");
    } catch (error) {
      console.error("Failed to update program:", error);
      toast.error(
        `❌ Failed to update program: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleCancel = () => {
    navigate("/programs");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!program) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          Program not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          mb: 4,
        }}
      >
        <Typography variant='h4' fontWeight='700' gutterBottom>
          Edit Program
        </Typography>
        <Typography variant='body1' sx={{ opacity: 0.9 }}>
          Update your wellness program
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <ProgramForm
          program={program}
          programCategories={programCategories}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </Paper>
    </Box>
  );
};

export default EditProgram;

